import { UserPermissions } from '@creditsights/api-ui-web';
import { CoverageIndicator, DataLoadError, Divider, Loader, PrintView } from '@creditsights/styleguide-web';
// eslint-disable-next-line import/no-unresolved
import { DealMarketStatus, getDealsTabs } from '@creditsights/cs-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../Base.constants';
import LinkGroup from '../../components/LinkGroup/LinkGroup';
import AnalystsWidget from '../../components/widgets/Analysts';
import { hasCompanyRiskMetrics } from '../../components/widgets/RiskProducts/RiskProductsWidget.logic';
import useFetchAnalysts from '../../hooks/analyst/useFetchAnalysts';
import { useFetchArticles } from '../../hooks/articles';
import useFetchCompanyDeals from '../../hooks/deals/useFetchCompanyDeals';
import useFetchCases from '../../hooks/dockets/useFetchCases';
import useFetchBonds from '../../hooks/documents/useFetchBonds';
import useFetchLoans from '../../hooks/documents/useFetchLoans';
import useFetchFinancials from '../../hooks/financials/useFetchFinancials';
import useFetchFinancialsSegments from '../../hooks/financials/useFetchFinancialsSegments';
import { useFetchIssuer, useFetchPageComponents } from '../../hooks/issuers';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import { useFetchForPrint } from '../../hooks/print';
import useFetchRiskProducts from '../../hooks/riskProducts/useFetchRiskProducts';
import useFetchFilters from '../../hooks/tags/useFetchFilters';
import { ContentTypeFilter, IssuerType, PageComponentsMap } from '../../models';
import { CompanyPageParams } from '../../models/CompanyPageParams';
import { IssuerDto } from '../../services/dtos/issuer/issuer.dto';
import { isNotFound } from '../../utils/navigationUtils';
import { hasPermissionToDockets, hasPermissionToRP } from '../../utils/permissionsUtils';
import { getIssuerTag } from '../../utils/tagUtils';
import { safeDecode } from '../../utils/urlUtils';
import {
  getIssuerRoutes,
  isCompanyCovered,
  isCompanyRoute,
  isDealsRoute,
  hideCSView,
  isDocketsRoute,
  isFinancialsCovered,
} from './Company.logic';
import CompanyAddToWatchlistAction from './CompanyAddToWatchlistAction/CompanyAddToWatchlistAction';
import { CompanyDealsContextProvider } from './CompanyDealsContext';
import {
  getDefaultFetchBondsPayload,
  getDefaultFetchLoansPayload,
} from './CompanyDocumentsTab/CompanyDocumentsTab.logic';
import CompanyHeader from './CompanyHeader';
import CompanyOverviewTab from './CompanyOverviewTab';
import CompanyPrintAction from './CompanyPrintAction';
import { STATIC_PRINTABLE_SECTIONS } from './CompanyPrintAction/CompanyPrintAction.constants';
import { RecentlyPublishedContextProvider } from './CompanyRecentlyPublishedContext';
import { CompanyRiskProductsContextProvider } from './CompanyRiskProductsContext';
import { getContentTypeTagFilter } from './RecentlyPublished.logic';
import styles from './Company.module.scss';

interface CompanyProps {
  issuerType: IssuerType;
}

export interface CompanyOutletContext {
  issuerTag: string;
  company: IssuerDto;
  print?: boolean;
}

const cx = classNames.bind(styles);

const renderCompany = (
  company: IssuerDto | undefined,
  issuerId: string,
  issuerTag: string,
  issuerType: IssuerType,
  pathname: string,
  pageComponentsMap: PageComponentsMap | undefined,
  isAnalystsWidgetRendered: boolean,
  setIsAnalystsWidgetRendered: (value: boolean) => void,
  hasDeals: boolean,
  hasWidgetDeals: boolean,
  hasFinancialSegments: boolean,
  hasRiskProducts: boolean,
  isDocumentsTabVisible: boolean,
  isDocketsTabVisible: boolean,
  userName: string,
  disclaimer: string | undefined,
  redistributionAllowed: boolean,
  userPermissions: UserPermissions | undefined
) => {
  const isOverviewActiveTab: boolean =
    (pathname === ROUTES.company(issuerId) || pathname === ROUTES.company(`${issuerId}/`)) && isCompanyCovered(company);

  return company ? (
    <>
      <div className={cx('header')}>
        <CompanyHeader className="no-print" name={company.displayName} ticker={company.displayTicker} />
        <div className={cx('action-buttons-wrapper')}>
          {isCompanyCovered(company) && (
            <>
              <CoverageIndicator className="no-print" {...company} />
              <Divider vertical className="no-print" />
            </>
          )}
          <AnalystsWidget
            analysts={company.analysts}
            minimized
            setIsAnalystsWidgetRendered={setIsAnalystsWidgetRendered}
            hideDeactivatedAnalyst
            askAnalystModalSubject={{
              companyId: issuerTag,
              subjectName: company.displayName,
            }}
          />
          {isAnalystsWidgetRendered && <Divider vertical className="no-print" />}
          {isOverviewActiveTab && (
            <CompanyPrintAction
              pageComponentsMap={pageComponentsMap}
              company={company}
              hiddenSections={{
                [STATIC_PRINTABLE_SECTIONS.DEALS.id]: !hasWidgetDeals,
                [STATIC_PRINTABLE_SECTIONS.RISK_PRODUCTS.id]: !hasRiskProducts,
                [STATIC_PRINTABLE_SECTIONS.CS_VIEW.id]: hideCSView(company),
              }}
            />
          )}
          <CompanyAddToWatchlistAction issuerTag={issuerTag} />
        </div>
        <div className={cx('tabs-wrapper')} data-testid="company-tabs-wrapper">
          <LinkGroup
            routes={getIssuerRoutes(
              issuerType,
              issuerId,
              isCompanyCovered(company),
              isFinancialsCovered(company),
              hasDeals,
              isDocumentsTabVisible,
              isDocketsTabVisible,
              userPermissions
            )}
          />
        </div>
      </div>
      <Outlet context={{ issuerTag, company }} />
      {!isOverviewActiveTab && (
        <PrintView
          isHeaderFooterVisible
          userFullName={userName}
          disclaimer={disclaimer}
          redistributionAllowed={redistributionAllowed}
        >
          <CompanyHeader name={company.displayName} ticker={company.displayTicker} />
          <AnalystsWidget analysts={company.analysts} print />
          <Divider />
          <Outlet context={{ issuerTag, company, print: true }} />
        </PrintView>
      )}
      {isOverviewActiveTab && (
        <CompanyOverviewTab
          company={company}
          pageComponentsMap={pageComponentsMap}
          hasDeals={hasWidgetDeals}
          hasFinancialSegments={hasFinancialSegments}
          hasRiskProducts={hasRiskProducts}
        />
      )}
    </>
  ) : null;
};

const Company = ({ issuerType }: CompanyProps): React.ReactElement => {
  const [isAnalystsWidgetRendered, setIsAnalystsWidgetRendered] = useState<boolean>(false);
  const { issuerId } = useParams() as CompanyPageParams;
  const navigate = useNavigate();
  const issuerTag = getIssuerTag(issuerId, issuerType);
  const {
    data: company,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    refetch: refetchCompany,
    error,
  } = useFetchIssuer(issuerTag);

  const {
    data: pageComponentsMap,
    isLoading: isLoadingComponents,
    isError: isErrorComponents,
    refetch: refetchComponents,
  } = useFetchPageComponents(issuerTag, {
    enabled: !!(company?.canAccess && company?.canAccessCompanyPage && company?.hasCompanyPage),
  });
  const {
    isLoading: isLoadingRecentlyPublished,
    isError: isErrorRecentlyPublished,
    refetch: refetchRecentlyPublished,
  } = useFetchArticles({
    take: 25,
    page: 0,
    filters: {
      tags: getContentTypeTagFilter(ContentTypeFilter.ALL),
      focusTagId: issuerTag,
    },
    enabled: company?.canAccess,
  });
  const { isLoading: isLoadingTagFilters } = useFetchFilters([]);
  const { isLoading: isLoadingAnalysts } = useFetchAnalysts({
    analystIds: company?.analysts || [],
  });
  const { data: userPermissions } = useFetchUserPermissions();
  const {
    isLoading: isLoadingDeals,
    isError: isErrorDeals,
    refetch: refetchDeals,
    data: deals,
  } = useFetchCompanyDeals(issuerId, {
    take: 1,
    enabled: company?.canAccess,
  });
  const {
    data: bondsDocuments,
    isLoading: isLoadingBondsDocuments,
    refetch: refetchBondsDocuments,
    isError: isErrorFetchBonds,
  } = useFetchBonds(issuerId, getDefaultFetchBondsPayload(issuerId, 1));
  const {
    data: loansDocuments,
    isLoading: isLoadingLoansDocuments,
    refetch: refetchLoansDocuments,
    isError: isErrorFetchLoans,
  } = useFetchLoans(issuerId, getDefaultFetchLoansPayload(issuerId, 1));
  const tabConfig = getDealsTabs(userPermissions!);
  const marketStatuses = [DealMarketStatus.InMarket];
  if (tabConfig.includes(DealMarketStatus.Expected)) {
    marketStatuses.push(DealMarketStatus.Expected);
  }
  const {
    isLoading: isLoadingWidgetDeals,
    isError: isErrorWidgetDeals,
    refetch: refetchWidgetDeals,
    data: widgetDeals,
  } = useFetchCompanyDeals(issuerId, {
    marketStatuses,
    take: 1,
    enabled: company?.canAccess,
  });
  const {
    data: docketCases,
    isLoading: isLoadingDocketCases,
    isError: isErrorDocketCases,
  } = useFetchCases(issuerId, hasPermissionToDockets(userPermissions));
  const { data: riskMetrics } = useFetchRiskProducts(issuerId);
  const { data: financialsDto } = useFetchFinancials(issuerId);
  const segmentGroups = useFetchFinancialsSegments(financialsDto);

  const showRiskProductsWidget =
    hasPermissionToRP(userPermissions) &&
    !!riskMetrics &&
    hasCompanyRiskMetrics(riskMetrics.cqsMetrics, riskMetrics.creMetrics);

  const { userFullName, disclaimer, redistributionAllowed } = useFetchForPrint();

  const isLoading =
    isLoadingCompany ||
    isLoadingComponents ||
    isLoadingRecentlyPublished ||
    isLoadingTagFilters ||
    isLoadingAnalysts ||
    isLoadingDeals ||
    isLoadingWidgetDeals ||
    isLoadingBondsDocuments ||
    isLoadingLoansDocuments;

  const isError =
    isErrorCompany ||
    isErrorComponents ||
    isErrorRecentlyPublished ||
    isErrorDeals ||
    isErrorWidgetDeals ||
    isErrorFetchLoans ||
    isErrorFetchBonds;

  const refetch = () => {
    if (isErrorCompany) {
      refetchCompany();
    }
    if (isErrorComponents) {
      refetchComponents();
    }
    if (isErrorRecentlyPublished) {
      refetchRecentlyPublished();
    }
    if (isErrorDeals) {
      refetchDeals();
    }
    if (isErrorWidgetDeals) {
      refetchWidgetDeals();
    }
    if (isErrorFetchBonds) {
      refetchBondsDocuments();
    }
    if (isErrorFetchLoans) {
      refetchLoansDocuments();
    }
  };

  const { pathname } = useLocation();
  const hasDeals = !isEmpty(deals);
  const hasWidgetDeals = !isEmpty(widgetDeals);
  const hasFinancialSegments = !isEmpty(segmentGroups);
  const isDocumentsTabVisible = Boolean(bondsDocuments?.totalResults || loansDocuments?.totalResults);
  const isDocketsTabVisible =
    hasPermissionToDockets(userPermissions) && (!isEmpty(docketCases) || isErrorDocketCases || isLoadingDocketCases);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isNotFound(error)) {
      navigate(ROUTES.notFound, { replace: true });
      return;
    }
    if (isCompanyRoute(issuerId, pathname)) {
      const routes = getIssuerRoutes(
        issuerType,
        issuerId,
        isCompanyCovered(company),
        isFinancialsCovered(company),
        hasDeals,
        isDocumentsTabVisible,
        isDocketsTabVisible,
        userPermissions
      );
      if (routes[0] && routes[0].to !== pathname) {
        navigate(routes[0].to, { replace: true });
        return;
      }
    }
    if (isDealsRoute(issuerId, pathname) && !hasDeals) {
      navigate(ROUTES.notFound, { replace: true });
    }
    if (isDocketsRoute(issuerId, pathname) && !isDocketsTabVisible) {
      navigate(ROUTES.notFound, { replace: true });
    }
  }, [company, isLoading, error, pathname, isDocketsTabVisible]);

  if (isLoading) {
    return (
      <div className={cx('body')}>
        <div className={cx('loader-container')}>
          <Loader position="center" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cx('body')}>
        <DataLoadError onRetryClick={refetch} />
      </div>
    );
  }

  return (
    <RecentlyPublishedContextProvider>
      <CompanyDealsContextProvider>
        <CompanyRiskProductsContextProvider>
          <div className={cx('body')}>
            {renderCompany(
              company,
              issuerId,
              issuerTag,
              issuerType,
              safeDecode(pathname),
              pageComponentsMap,
              isAnalystsWidgetRendered,
              setIsAnalystsWidgetRendered,
              hasDeals,
              hasWidgetDeals,
              hasFinancialSegments,
              showRiskProductsWidget,
              isDocumentsTabVisible,
              isDocketsTabVisible,
              userFullName,
              disclaimer,
              redistributionAllowed,
              userPermissions
            )}
          </div>
        </CompanyRiskProductsContextProvider>
      </CompanyDealsContextProvider>
    </RecentlyPublishedContextProvider>
  );
};

export default Company;
